import { useEffect, useState } from "react";

/**
 * ONDI — a friendly, non-robotic 2D character.
 * - Blinking eyes (periodic)
 * - Waving hands on hover / open
 * - Colors follow --accent (mode-aware)
 */
export function OndiCharacter({
  size = 72,
  waving = false,
  talking = false,
  className = "",
  style = {},
}) {
  const [blink, setBlink] = useState(false);

  useEffect(() => {
    let alive = true;
    const scheduleNext = () => {
      const delay = 2500 + Math.random() * 3500;
      return setTimeout(() => {
        if (!alive) return;
        setBlink(true);
        setTimeout(() => alive && setBlink(false), 130);
        timer = scheduleNext();
      }, delay);
    };
    let timer = scheduleNext();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  const eyeH = blink ? 1 : 5;
  const mouthPath = talking
    ? "M 34 46 Q 42 52 50 46"
    : "M 34 46 Q 42 50 50 46";

  return (
    <div
      className={"ondi-character-wrapper pointer-events-none " + className}
      style={{
        width: size,
        height: size,
        filter:
          "drop-shadow(0 0 12px var(--accent-glow)) drop-shadow(0 6px 16px rgba(0,0,0,0.4))",
        ...style,
      }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 84 84"
        width={size}
        height={size}
        className={"select-none " + (talking ? "animate-[ondi-bob_1.4s_ease-in-out_infinite]" : "")}
      >
        {/* Body — soft blob */}
        <defs>
          <linearGradient id="ondi-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#111c33" stopOpacity="0.98" />
            <stop offset="1" stopColor="#0b1220" stopOpacity="0.98" />
          </linearGradient>
          <radialGradient id="ondi-glow" cx="0.5" cy="0.4" r="0.6">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.55" />
            <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Aura */}
        <ellipse cx="42" cy="45" rx="34" ry="30" fill="url(#ondi-glow)" />

        {/* Body */}
        <path
          d="M 20 40 Q 20 18 42 18 Q 64 18 64 40 L 64 52 Q 64 66 50 66 L 34 66 Q 20 66 20 52 Z"
          fill="url(#ondi-body)"
          stroke="var(--accent)"
          strokeWidth="1.8"
        />

        {/* Cheeks */}
        <circle cx="28" cy="46" r="3" fill="var(--accent)" opacity="0.55" />
        <circle cx="56" cy="46" r="3" fill="var(--accent)" opacity="0.55" />

        {/* Eyes */}
        <g fill="var(--accent)">
          <ellipse cx="34" cy="38" rx="3" ry={eyeH} />
          <ellipse cx="50" cy="38" rx="3" ry={eyeH} />
        </g>
        {/* eye sparkle */}
        {!blink && (
          <g fill="#ffffff" opacity="0.85">
            <circle cx="35" cy="36.5" r="0.9" />
            <circle cx="51" cy="36.5" r="0.9" />
          </g>
        )}

        {/* Mouth */}
        <path
          d={mouthPath}
          stroke="var(--accent)"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />

        {/* Left hand */}
        <g
          className={waving ? "animate-[ondi-wave_1s_ease-in-out_infinite]" : ""}
          style={{ transformOrigin: "20px 55px" }}
        >
          <circle cx="18" cy="55" r="5.5" fill="var(--accent)" />
        </g>

        {/* Right hand */}
        <g
          className={waving ? "animate-[ondi-wave-r_1s_ease-in-out_infinite]" : ""}
          style={{ transformOrigin: "66px 55px" }}
        >
          <circle cx="66" cy="55" r="5.5" fill="var(--accent)" />
        </g>

        {/* Little antenna */}
        <line x1="42" y1="18" x2="42" y2="10" stroke="var(--accent)" strokeWidth="1.5" />
        <circle cx="42" cy="8" r="2.5" fill="var(--accent)" />
      </svg>

      <style>{`
        @keyframes ondi-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes ondi-wave {
          0%, 100% { transform: rotate(-8deg); }
          50% { transform: rotate(18deg); }
        }
        @keyframes ondi-wave-r {
          0%, 100% { transform: rotate(8deg); }
          50% { transform: rotate(-18deg); }
        }
      `}</style>
    </div>
  );
}
